import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dynamic from 'next/dynamic';

import {
  trackCloseContactPartnerModal,
  trackContactPartnerSubmitFulfilled,
  trackContactPartnerValidationError,
  trackMarketingPreferencesImpression,
  trackSubmitMarketingPreferences,
} from 'src/components/molecules/ContactPartnerModal/tracking/contactPartnerModalEvents';
import type { ContactPartnerSubmitPayload } from 'src/components/molecules/ContactPartnerModal/types';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectContactFormServerErrors } from 'src/redux/contactPartner/selectors';
import { actions } from 'src/redux/contactPartner/slice';
import { contactPartnerSubmit } from 'src/redux/contactPartner/thunks';
import { selectProduct } from 'src/redux/product/selectors';
import { ModalLoader } from '../ModalLoader/ModalLoader';

const ContactPartnerModal = dynamic(
  async () =>
    (await import(/* webpackChunkName: "ContactPartnerModal" */ './ContactPartnerModal'))
      .ContactPartnerModal,
  { loading: () => <ModalLoader /> },
);

export const ContactPartnerModalContainer = () => {
  const dispatch = useDispatch();
  const state = useSelector((state: ReduxApplicationState) => state);
  const product = selectProduct(state);
  const partnerName = product.partner.name;
  const contactFormServerErrors = selectContactFormServerErrors(state);
  const isContactPartnerModalOpen = state.contactPartnerModal.isContactPartnerModalOpen;
  const messageSentSuccessfully = state.contactPartnerModal.messageSent;

  const onContactPartnerSubmit = async (payload: ContactPartnerSubmitPayload) => {
    await dispatch(contactPartnerSubmit(payload));
    trackContactPartnerSubmitFulfilled(partnerName);
    if (payload.marketingOptIn && payload.userAttributes?.email) {
      trackSubmitMarketingPreferences(payload.userAttributes.email);
    }
  };

  const onContactPartnerSubmitError = (payload: string) => {
    trackContactPartnerValidationError(payload);
  };

  const closeContactPartnerModal = () => {
    dispatch(actions.closeContactPartnerModal());
    trackCloseContactPartnerModal(partnerName);
  };

  return (
    <>
      {isContactPartnerModalOpen && (
        <ContactPartnerModal
          isModalOpen={isContactPartnerModalOpen}
          messageSentSuccessfully={messageSentSuccessfully}
          onModalClose={closeContactPartnerModal}
          onSubmit={onContactPartnerSubmit}
          onSubmitError={onContactPartnerSubmitError}
          onVisible={trackMarketingPreferencesImpression}
          product={product}
          serverErrors={contactFormServerErrors}
        />
      )}
    </>
  );
};
